import React, { useState, useRef, useEffect } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { useTranslation } from "react-i18next";
import { classNames } from "../../helpers";
import { useNavigationHelpers } from "../../hooks/useNavigationHelpers";

interface AudioRecorderProps {
  onRecordingComplete: (blobUrl: string) => void;
  onError: (error: string) => void;
  previewRoute?: string; // Optional preview route, defaults to '/preview'
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onRecordingComplete,
  onError,
  previewRoute = '/preview',
}) => {
  const { t } = useTranslation("", { keyPrefix: "page.readingRecorder" });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const timerRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>("");
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cleanupRef = useRef<boolean>(false);
  const { t: button } = useTranslation("", {
    keyPrefix: "page.readingRecorder",
  });
  const { handleBack } = useNavigationHelpers();
  
  const {
    status,
    startRecording: startMediaRecording,
    stopRecording: stopMediaRecording,
    mediaBlobUrl: recorderBlobUrl,
    clearBlobUrl,
    error,
  } = useReactMediaRecorder({
    audio: {
      deviceId: selectedMic ? { exact: selectedMic } : undefined,
      echoCancellation: true,
      noiseSuppression: true,
      // sampleRate: 44100, // Consistent sample rate for iOS
      // channelCount: 1, // Mono for better iOS compatibility
    },
    // askPermissionOnMount: false, // Prevent automatic permission request
    onStop: (blobUrl: string) => {
      console.log('Recording stopped, processing blob URL:', blobUrl);
      
      // Convert blob URL to base64 data for storage across page reload
      fetch(blobUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch blob: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          console.log('Blob created:', blob.size, 'bytes, type:', blob.type);
          
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result as string;
            console.log('Base64 data created, length:', base64data.length);
            
            // Store the base64 data instead of blob URL
            localStorage.setItem('recordedAudioData', base64data);
            console.log('Audio data stored in localStorage');
            
            // Navigate to preview page with reload
            setTimeout(() => {
              window.location.href = previewRoute;
            }, 100);
          };
          reader.onerror = () => {
            console.error('FileReader error:', reader.error);
            onError('Failed to process recording');
          };
          reader.readAsDataURL(blob);
        })
        .catch(error => {
          console.error('Error converting blob to base64:', error);
          onError('Failed to process recording');
        });
    },
  });

  const cleanupAudioContext = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };


  const initAudioContext = (stream: MediaStream) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    const audioContext = audioContextRef.current;
    if (analyserRef.current) {
      analyserRef.current.disconnect();
    }
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.minDecibels = -80;
    analyser.maxDecibels = -10;
    analyser.smoothingTimeConstant = 0.8;

    analyserRef.current = analyser;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    return analyser;
  };

  const drawWaveform = () => {
    if (cleanupRef.current || !canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const barWidth = (canvas.width / bufferLength) * 2.5;
    const cornerRadius = 5;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      let barHeight = dataArray[i];
      barHeight = (barHeight / 255) * canvas.height * 0.8;
      barHeight = Math.max(barHeight, 2);

      const barX = x;
      const barY = canvas.height - barHeight;

      ctx.beginPath();
      ctx.moveTo(barX + cornerRadius, barY);
      ctx.arcTo(
        barX + barWidth,
        barY,
        barX + barWidth,
        barY + cornerRadius,
        cornerRadius
      );
      ctx.lineTo(barX + barWidth, barY + barHeight);
      ctx.lineTo(barX, barY + barHeight);
      ctx.arcTo(barX, barY, barX + cornerRadius, barY, cornerRadius);
      ctx.closePath();

      ctx.fillStyle = `rgb(99, 102, 241)`;
      ctx.fill();

      x += barWidth + 1.5;
    }

    if (!cleanupRef.current) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  };

  const startPreview = async () => {
    if (isRecording) return; // Don't start preview while recording
    
    try {
      cleanupRef.current = false;
      
      // Stop existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          // sampleRate: 44100,
          // channelCount: 1,
        },
      });

      streamRef.current = stream;
      const analyser = initAudioContext(stream);
      
      if (analyser) {
        drawWaveform();
      }
      
      setIsPreviewActive(true);
    } catch (error) {
      console.error("Error starting preview:", error);
      onError(
        "Failed to start preview. Please check your microphone permissions."
      );
    }
  };

  const stopPreview = () => {
    cleanupRef.current = true;
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    // Make sure to cleanup the audio context
    cleanupAudioContext();
    setIsPreviewActive(false);
    
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  };

  const stopAllMediaTracks = () => {
    stopPreview();
    setIsRecording(false);
  };

  // Request permissions and setup devices
  useEffect(() => {
    const requestPermissions = async () => {
      try {
        // Only request audio permissions - no video
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => track.stop());
        setPermissionsGranted(true);
        getDevices();
      } catch (error) {
        console.error("Permission error:", error);
        setPermissionError(t("microphoneCameraRequired"));
      }
    };

    requestPermissions();

    const handleBeforeUnload = () => {
      stopAllMediaTracks();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      stopAllMediaTracks();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Handle preview state changes - only for initial preview, not for recorded audio
  useEffect(() => {
    if (permissionsGranted && !isRecording) {
      startPreview();
    }
  }, [permissionsGranted, selectedMic]); // Removed isRecording from dependencies

  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setDevices(devices);

      const mics = devices.filter((d) => d.kind === "audioinput" && d.deviceId);
      setSelectedMic((prev) => prev || mics[0]?.deviceId || "");
    } catch (error) {
      console.error("Error enumerating devices:", error);
      onError("Failed to access media devices");
    }
  };

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      startTimeRef.current = Date.now();
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        if (elapsed >= 300) {
          handleStopRecording();
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (error) {
      console.error("Media recorder error:", error);
      onError(typeof error === "string" ? error : "Failed to record audio");
    }
  }, [error, onError]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleStartRecording = async () => {
    // Resume audio context for iOS before recording
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    
    setIsRecording(true);
    clearBlobUrl();
    // Don't stop preview during recording - keep waveform showing
    startMediaRecording();
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Clean up audio context and streams before navigation
    stopPreview();
    cleanupAudioContext();
    
    stopMediaRecording();
    // The onStop callback will handle navigation to preview page
  };

  const handleMicChange = async (deviceId: string) => {
    setSelectedMic(deviceId);
  };

  return (
    <div className="flex flex-col items-center justify-between w-full h-full gap-4">
      {permissionError ? (
        <div className="w-full max-w-2xl p-4 mb-4 text-red-800 bg-red-100 rounded-lg">
          <p>{permissionError}</p>
          <button
            onClick={() => {
              setPermissionError(null);
              navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then((stream) => {
                  streamRef.current = stream;
                  stream.getTracks().forEach((track) => track.stop());
                  setPermissionsGranted(true);
                  getDevices();
                  startPreview();
                })
                .catch((error) => {
                  setPermissionError(t("microphoneCameraRequired"));
                });
            }}
            className="px-4 py-2 mt-2 text-white bg-red-600 rounded"
          >
            {t("retryPermissions")}
          </button>
        </div>
      ) : (
        <>
          {!isRecording && (
            <div className="flex flex-col w-full max-w-2xl gap-4 sm:flex-row">
              <select
                dir="ltr"
                className="w-full p-2 border rounded pe-10"
                onChange={(e) => handleMicChange(e.target.value)}
                value={selectedMic}
              >
                {devices
                  .filter((d) => d.kind === "audioinput")
                  .map((mic) => (
                    <option key={mic.deviceId} value={mic.deviceId}>
                      {mic.label || "Microphone"}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div className="w-full max-w-2xl">
            <canvas
              ref={canvasRef}
              width={800}
              height={100}
              className="w-full h-24 bg-gray-100 rounded-lg"
            />
          </div>
        </>
      )}

      <div
        className={classNames(
          "z-20 px-10 py-4 mt-[300px]  justify-between items-center md:px-0 w-full  sticky flex-wrap border-t border-gray-200 bottom-0 bg-white gap-3 flex mx-auto "
        )}
      >
        <button
          onClick={handleBack}
          className="px-6 py-2 text-sm text-gray-700 border-2 border-gray-300 rounded-md whitespace-nowrap md:px-4"
        >
          {button("back")}
        </button>

        {isRecording && (
          <div className="flex items-center gap-2 p-2 bg-opacity-50 rounded ">
            <span className="text-gray-500 text-[32px] font-semibold">
              {formatTime(recordingTime)}
            </span>
          </div>
        )}

        {!isRecording && permissionsGranted && (
          <button
            onClick={handleStartRecording}
            className="px-4 py-2 text-sm text-white rounded-md bg-brandblue-600 ms-auto"
          >
            {t("recordingStart")}
          </button>
        )}

        {isRecording && (
          <button
            onClick={handleStopRecording}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded"
          >
            {t("stopRegistration")}
          </button>
        )}
      </div>
    </div>
  );
};

export default AudioRecorder;
